import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { raBillTemplatesAPI } from '../../../services/authService';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';
import {
  compileTemplateFields,
  hydrateTemplateFields,
  uniqueCode,
  suggestFieldCode,
} from './adapters';
import TemplateListPanel from './components/TemplateListPanel';
import BillStructureCanvas from './components/BillStructureCanvas';
import FieldPropertiesPanel from './components/FieldPropertiesPanel';
import AddSectionDialog from './components/AddSectionDialog';
import AddFieldWizard from './components/AddFieldWizard';
import CloneTemplateDialog from './components/CloneTemplateDialog';
import PresetPickerDialog from './components/PresetPickerDialog';
import ValidationResultsPanel from './components/ValidationResultsPanel';
import RelationshipMapView from './components/RelationshipMapView';
import TemplatePreviewBill from './components/TemplatePreviewBill';
import './raBillTemplates.css';

const normalizeDraft = (data) => {
  const sections = (data.sections || []).slice().sort((a, b) => a.sequence - b.sequence);
  const fields = hydrateTemplateFields(
    (data.fields || []).map((f) => {
      const sec = sections.find((s) => s.id === f.sectionId);
      return { ...f, sectionCode: sec?.sectionCode || f.sectionCode || '' };
    }).sort((a, b) => a.sequence - b.sequence),
  );
  return { ...data, sections, fields };
};

const RaBillTemplatesShell = ({ canAccess }) => {
  const [tab, setTab] = useState('designer');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedFieldCode, setSelectedFieldCode] = useState('');
  const [validation, setValidation] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ code: '', name: '', calculationEngine: 'CONFIGURABLE' });
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [addFieldSection, setAddFieldSection] = useState('');
  const [cloneSource, setCloneSource] = useState(null);
  const [presetOpen, setPresetOpen] = useState(false);
  const [relMapOpen, setRelMapOpen] = useState(false);

  const isPrivileged = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const role = String(user.role || user.userRole || '').toUpperCase();
      const codes = (user.roles || []).map((r) => String(r.code || r).toUpperCase());
      return role === 'SUPER_ADMIN' || role === 'ADMIN' || codes.includes('SUPER_ADMIN') || codes.includes('ADMIN');
    } catch (_) {
      return false;
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await raBillTemplatesAPI.list();
      setRows(resp?.data?.data || resp?.data || []);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) loadList();
  }, [canAccess, loadList]);

  const openTemplate = async (id) => {
    try {
      const resp = await raBillTemplatesAPI.getById(id);
      const data = normalizeDraft(resp?.data?.data || resp?.data);
      setSelectedId(id);
      setDraft(data);
      setSelectedFieldCode(data.fields?.[0]?.fieldCode || '');
      setValidation(null);
      setPreviewResult(null);
      setTab('designer');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to open template');
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    const raw = sessionStorage.getItem('openRaBillTemplateId');
    if (!raw) return;
    sessionStorage.removeItem('openRaBillTemplateId');
    const id = Number(raw);
    if (Number.isFinite(id) && id > 0) openTemplate(id);
  }, [canAccess]);

  const [activeEditUnlocked, setActiveEditUnlocked] = useState(false);

  useEffect(() => {
    // Re-lock when switching templates
    setActiveEditUnlocked(false);
  }, [draft?.id]);

  const selectedField = useMemo(
    () => (draft?.fields || []).find((f) => f.fieldCode === selectedFieldCode) || null,
    [draft, selectedFieldCode],
  );

  const sectionsSorted = useMemo(
    () => (draft?.sections || []).slice().sort((a, b) => a.sequence - b.sequence),
    [draft],
  );

  const isLiveStatus = Boolean(draft && ['ACTIVE', 'PUBLISHED'].includes(draft.status));
  const isSuperseded = Boolean(draft && draft.status === 'SUPERSEDED');
  const canUnlockActiveEdit = Boolean(
    draft
    && isLiveStatus
    && !draft.isSystemTemplate
  );
  const readOnly = Boolean(
    draft
    && (
      isSuperseded
      || (['ACTIVE', 'PUBLISHED'].includes(draft.status) && !activeEditUnlocked)
      || (draft.isSystemTemplate && isLiveStatus)
    ),
  );

  const unlockActiveEdit = async () => {
    if (!canUnlockActiveEdit) return;
    const ok = await confirmDialog(
      'Edit this ACTIVE template in place?\n\n'
      + '• New RA bills will use your changes immediately after Save.\n'
      + '• Existing RA bills keep their frozen snapshot and will not change.\n'
      + '• Prefer Create New Version for production rollouts.',
      'Edit current template',
    );
    if (!ok) return;
    setActiveEditUnlocked(true);
    notifySuccess('Editing unlocked. Save when ready — status stays ACTIVE.');
  };

  const updateField = (fieldCode, patch) => {
    setDraft((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((f) => (f.fieldCode === fieldCode ? { ...f, ...patch } : f)),
    }));
  };

  const renameFieldCode = (nextCode) => {
    const prevCode = selectedFieldCode;
    if (!nextCode || nextCode === prevCode) return;
    setDraft((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.fieldCode === prevCode ? { ...f, fieldCode: nextCode } : f)),
    }));
    setSelectedFieldCode(nextCode);
  };

  const moveField = (fieldCode, target) => {
    setDraft((prev) => {
      const fields = (prev.fields || []).slice().sort((a, b) => a.sequence - b.sequence);
      const idx = fields.findIndex((f) => f.fieldCode === fieldCode);
      if (idx < 0) return prev;
      if (typeof target === 'number') {
        const swap = idx + target;
        if (swap < 0 || swap >= fields.length) return prev;
        const seqA = fields[idx].sequence;
        const seqB = fields[swap].sequence;
        fields[idx] = { ...fields[idx], sequence: seqB };
        fields[swap] = { ...fields[swap], sequence: seqA };
        return { ...prev, fields };
      }
      const toIdx = fields.findIndex((f) => f.fieldCode === target);
      if (toIdx < 0) return prev;
      const [item] = fields.splice(idx, 1);
      fields.splice(toIdx, 0, item);
      return {
        ...prev,
        fields: fields.map((f, i) => ({ ...f, sequence: (i + 1) * 10 })),
      };
    });
  };

  const duplicateField = (fieldCode) => {
    setDraft((prev) => {
      const src = (prev.fields || []).find((f) => f.fieldCode === fieldCode);
      if (!src) return prev;
      const code = uniqueCode(`${src.fieldCode}_COPY`, prev.fields.map((f) => f.fieldCode));
      const copy = {
        ...JSON.parse(JSON.stringify(src)),
        id: undefined,
        fieldCode: code,
        fieldLabel: `${src.fieldLabel || src.fieldCode} (copy)`,
        sequence: (src.sequence || 0) + 5,
      };
      return { ...prev, fields: [...prev.fields, copy] };
    });
  };

  const deactivateField = (fieldCode) => {
    setDraft((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => {
        if (f.fieldCode !== fieldCode) return f;
        const next = String(f.status || 'ACTIVE').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        return { ...f, status: next };
      }),
    }));
  };

  const saveDraft = async ({ silent = false } = {}) => {
    if (!draft?.id) return false;
    if (readOnly) {
      if (!silent) notifyError('This template version is locked. Create a new version or unlock Edit current template.');
      return false;
    }
    setSaving(true);
    try {
      const compiledFields = compileTemplateFields(draft.fields || []).map((f) => {
        // Drop stale DB sectionId — backend remaps via sectionCode after recreate.
        const { sectionId, id, ...rest } = f;
        return rest;
      });
      const payload = {
        name: draft.name,
        description: draft.description,
        calculationEngine: draft.calculationEngine,
        sections: (draft.sections || []).map((s) => {
          const { id, ...rest } = s;
          return rest;
        }),
        fields: compiledFields,
        ...(activeEditUnlocked && isLiveStatus ? { allowActiveEdit: true } : {}),
      };
      const resp = await raBillTemplatesAPI.update(draft.id, payload);
      const data = normalizeDraft(resp?.data?.data || resp?.data);
      if (!silent) {
        notifySuccess(
          activeEditUnlocked && isLiveStatus
            ? 'Active template updated. New bills will use these rules; existing bills stay frozen.'
            : 'Draft saved',
        );
      }
      setDraft(data);
      if (selectedFieldCode && !data.fields.some((f) => f.fieldCode === selectedFieldCode)) {
        setSelectedFieldCode(data.fields[0]?.fieldCode || '');
      }
      loadList();
      return true;
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async (forPublish = false) => {
    if (!draft?.id) return;
    try {
      if (!readOnly) {
        const ok = await saveDraft({ silent: true });
        if (!ok) return;
      }
      const resp = await raBillTemplatesAPI.validate(draft.id, { forPublish });
      const result = resp?.data || resp;
      setValidation(result);
      if (result.ok) {
        notifySuccess(forPublish ? 'Ready to publish' : 'Validation passed');
      } else {
        const first = (result.errors || [])[0];
        notifyWarning(
          first
            ? `${first}${result.errors.length > 1 ? ` (+${result.errors.length - 1} more — see details below)` : ''}`
            : 'Validation found issues — see details below',
          'Needs attention',
        );
      }
      loadList();
    } catch (err) {
      // API returns HTTP 400 when validation fails — treat body as normal result.
      const details = err?.response?.data;
      if (details && (Array.isArray(details.errors) || details.ok === false)) {
        setValidation(details);
        const first = (details.errors || [])[0];
        notifyWarning(
          first
            ? `${first}${details.errors.length > 1 ? ` (+${details.errors.length - 1} more — see details below)` : ''}`
            : 'Validation found issues — see details below',
          'Needs attention',
        );
        return;
      }
      notifyError(details?.message || err?.message || 'Validation failed');
    }
  };

  const runPreviewWithValues = async (inputs = {}, previousValues = {}) => {
    if (!draft?.id) return;
    try {
      if (!readOnly) {
        // Ensure latest formulas exist server-side
        const compiledFields = compileTemplateFields(draft.fields || []).map((f) => {
          const { sectionId, id, ...rest } = f;
          return rest;
        });
        await raBillTemplatesAPI.update(draft.id, {
          name: draft.name,
          description: draft.description,
          calculationEngine: draft.calculationEngine,
          sections: (draft.sections || []).map((s) => {
            const { id, ...rest } = s;
            return rest;
          }),
          fields: compiledFields,
        });
      }
      const resp = await raBillTemplatesAPI.preview(draft.id, {
        inputs,
        previousValues,
        legacyContext: {
          agreementAmount: Number(inputs.AGREEMENT_AMOUNT || 100000000),
          commercialInputs: {
            gross_value_of_work_done: Number(inputs.GROSS_WORK_DONE || inputs.upto_date_gross_work_done || 0),
            upto_date_secured_advance_on_material_brought_to_site: Number(inputs.SECURED_ADVANCE || 0),
            mobilization_advance: Number(inputs.MOBILISATION_ADVANCE || 0),
            clause_10cc_10ca_payment: Number(inputs.CLAUSE_10CC_10CA || 0),
            advance_payment_work_done_but_not_measured: Number(inputs.ADVANCE_NOT_MEASURED || 0),
          },
          isFirstBill: Object.keys(previousValues || {}).length === 0,
          totalWithheld: Number(inputs.TOTAL_WITHHELD || 0),
          addlSdForSpecialItems: Number(inputs.ADDL_SD_SPECIAL || 0),
        },
      });
      setPreviewResult(resp?.data || resp);
      setTab('preview');
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Preview failed');
    }
  };

  const runPublish = async () => {
    if (!draft?.id) return;
    if (!(await confirmDialog('Publish this template version? Prior active versions of the same code will be superseded.', 'Publish Template'))) return;
    try {
      if (!readOnly) {
        const ok = await saveDraft({ silent: true });
        if (!ok) return;
      }
      try {
        const pre = await raBillTemplatesAPI.validate(draft.id, { forPublish: true });
        const result = pre?.data || pre;
        setValidation(result);
        if (!result.ok) {
          notifyWarning(
            (result.errors || [])[0] || 'Fix validation issues before publishing',
            'Not ready to publish',
          );
          return;
        }
      } catch (err) {
        const details = err?.response?.data;
        if (details && (Array.isArray(details.errors) || details.ok === false)) {
          setValidation(details);
          notifyWarning(
            (details.errors || [])[0] || 'Fix validation issues before publishing',
            'Not ready to publish',
          );
          return;
        }
        throw err;
      }
      await raBillTemplatesAPI.publish(draft.id);
      notifySuccess('Published');
      await openTemplate(draft.id);
      loadList();
    } catch (err) {
      const details = err?.response?.data;
      if (details?.details) setValidation(details.details);
      notifyError(details?.message || 'Publish failed');
    }
  };

  const runClone = async (payload) => {
    const id = cloneSource?.id || payload?.id;
    if (!id) return;
    try {
      const resp = await raBillTemplatesAPI.clone(id, typeof payload === 'object' ? payload : {});
      const data = resp?.data?.data || resp?.data;
      notifySuccess(`Cloned as ${data.code}`);
      setCloneSource(null);
      loadList();
      openTemplate(data.id);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Clone failed');
    }
  };

  const runCreateVersion = async (id) => {
    try {
      const resp = await raBillTemplatesAPI.createVersion(id, {});
      const data = resp?.data?.data || resp?.data;
      notifySuccess(`Created ${data.code} v${data.version}`);
      loadList();
      openTemplate(data.id);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Create version failed');
    }
  };

  const createNew = async () => {
    if (!createForm.code || !createForm.name) {
      notifyError('Code and name are required');
      return;
    }
    try {
      const resp = await raBillTemplatesAPI.create({
        ...createForm,
        code: suggestFieldCode(createForm.code) || createForm.code,
      });
      const data = resp?.data?.data || resp?.data;
      notifySuccess('Template created');
      setCreateForm({ code: '', name: '', calculationEngine: 'CONFIGURABLE' });
      loadList();
      openTemplate(data.id);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Create failed');
    }
  };

  const goToField = (code) => {
    setSelectedFieldCode(code);
    setTab('designer');
  };

  if (!canAccess) {
    return <div className="empty-state"><h3>Access denied</h3><p>RA Bill Template permissions are required.</p></div>;
  }

  return (
    <div className="ra-template-shell">
      <div className="section-header">
        <h2>RA Bill Templates <span className="count-badge">{rows.length}</span></h2>
      </div>
      <p className="ra-template-lead">
        Configure RA Bill fields using construction billing language. Formulas are generated automatically — no JSON required.
      </p>

      <div className="ra-tabs">
        <button type="button" className={`ra-tab ${tab === 'designer' ? 'is-active' : ''}`} onClick={() => setTab('designer')}>Designer</button>
        <button type="button" className={`ra-tab ${tab === 'preview' ? 'is-active' : ''}`} disabled={!draft} onClick={() => setTab('preview')}>Preview</button>
        <button type="button" className={`ra-tab ${tab === 'list' ? 'is-active' : ''}`} onClick={() => { setTab('list'); loadList(); }}>All Templates</button>
      </div>

      {tab === 'list' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Department</th>
                <th>Engine</th>
                <th>Version</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="ra-template-empty">Loading…</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong><div style={{ fontSize: 11, color: '#64748b' }}>{r.code}</div></td>
                  <td>{r.department?.code || '—'}</td>
                  <td>{r.calculationEngine === 'LEGACY_CPWD' ? 'CPWD' : 'Configurable'}</td>
                  <td>v{r.version}</td>
                  <td>{r.status}</td>
                  <td>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => openTemplate(r.id)}>Open</button>{' '}
                    <button type="button" className="btn-secondary btn-sm" onClick={() => setCloneSource(r)}>Clone</button>{' '}
                    <button type="button" className="btn-secondary btn-sm" onClick={() => runCreateVersion(r.id)}>New version</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'designer' && (
        <div className="ra-template-designer ra-template-designer--three">
          <TemplateListPanel
            rows={rows}
            loading={loading}
            selectedId={selectedId}
            createForm={createForm}
            setCreateForm={setCreateForm}
            onCreate={createNew}
            onOpen={openTemplate}
            onClone={(r) => setCloneSource(r)}
            onCreateVersion={runCreateVersion}
            onValidate={() => runValidate(false)}
            onPreview={() => setTab('preview')}
            onPublish={runPublish}
            draft={draft}
          />

          {draft ? (
            <>
              <div className="ra-tpl-center-col">
                <BillStructureCanvas
                  draft={draft}
                  sectionsSorted={sectionsSorted}
                  selectedFieldCode={selectedFieldCode}
                  readOnly={readOnly}
                  activeEditUnlocked={activeEditUnlocked}
                  canUnlockActiveEdit={canUnlockActiveEdit}
                  onUnlockActiveEdit={unlockActiveEdit}
                  onSelectField={setSelectedFieldCode}
                  onMoveField={moveField}
                  onDuplicateField={duplicateField}
                  onDeactivateField={deactivateField}
                  onOpenAddSection={() => setAddSectionOpen(true)}
                  onOpenAddField={(sectionCode) => { setAddFieldSection(sectionCode); setAddFieldOpen(true); }}
                  onOpenPresets={() => setPresetOpen(true)}
                  onSave={saveDraft}
                  saving={saving}
                  onCreateVersion={runCreateVersion}
                />
                <ValidationResultsPanel
                  validation={validation}
                  fields={draft.fields}
                  onGoToField={goToField}
                  onApplyRoleSuggestions={(suggestions) => {
                    setDraft((prev) => ({
                      ...prev,
                      fields: (prev.fields || []).map((f) => {
                        const hit = suggestions.find((s) => s.fieldCode === f.fieldCode);
                        return hit ? { ...f, semanticRole: hit.role } : f;
                      }),
                    }));
                    notifySuccess('Suggested System Meanings applied — Save draft, then Publish');
                  }}
                />
              </div>
              <FieldPropertiesPanel
                field={selectedField}
                fields={draft.fields}
                sections={sectionsSorted}
                draftStatus={draft.status}
                readOnly={readOnly}
                isPrivileged={isPrivileged}
                onChangeField={updateField}
                onCodeChange={renameFieldCode}
                onGoToField={goToField}
                onOpenRelationshipMap={() => setRelMapOpen(true)}
              />
            </>
          ) : (
            <div className="panel ra-tpl-canvas">
              <div className="empty-state">
                <h3>Select or create a template</h3>
                <p>Open a template from the left to configure the RA Bill structure.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'preview' && draft && (
        <TemplatePreviewBill
          draft={draft}
          previewResult={previewResult}
          isPrivileged={isPrivileged}
          onRunPreview={runPreviewWithValues}
        />
      )}

      <AddSectionDialog
        open={addSectionOpen}
        existingCodes={(draft?.sections || []).map((s) => s.sectionCode)}
        onClose={() => setAddSectionOpen(false)}
        onSubmit={(section) => {
          setDraft((prev) => ({ ...prev, sections: [...(prev.sections || []), section] }));
          setAddSectionOpen(false);
        }}
      />

      <AddFieldWizard
        open={addFieldOpen}
        sectionCode={addFieldSection}
        sections={sectionsSorted}
        existingCodes={(draft?.fields || []).map((f) => f.fieldCode)}
        onClose={() => setAddFieldOpen(false)}
        onSubmit={(field) => {
          setDraft((prev) => ({
            ...prev,
            fields: [
              ...(prev.fields || []),
              {
                ...field,
                sequence: ((prev.fields || []).filter((f) => f.sectionCode === field.sectionCode).length + 1) * 10,
              },
            ],
          }));
          setSelectedFieldCode(field.fieldCode);
        }}
      />

      <CloneTemplateDialog
        open={Boolean(cloneSource)}
        source={cloneSource}
        onClose={() => setCloneSource(null)}
        onSubmit={runClone}
      />

      <PresetPickerDialog
        open={presetOpen}
        sectionCode={addFieldSection || sectionsSorted[0]?.sectionCode}
        existingCodes={(draft?.fields || []).map((f) => f.fieldCode)}
        onClose={() => setPresetOpen(false)}
        onSubmit={(fields) => {
          setDraft((prev) => {
            let seqBase = (prev.fields || []).length;
            const next = fields.map((f, i) => ({ ...f, sequence: (seqBase + i + 1) * 10 }));
            return { ...prev, fields: [...(prev.fields || []), ...next] };
          });
          if (fields[0]) setSelectedFieldCode(fields[0].fieldCode);
        }}
      />

      <RelationshipMapView
        open={relMapOpen}
        fields={draft?.fields || []}
        onClose={() => setRelMapOpen(false)}
        onGoToField={goToField}
      />
    </div>
  );
};

export default RaBillTemplatesShell;
